import React from 'react';
import { Alert, Badge, Button, Spinner, Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { IconDeviceDesktop, IconLogout2 } from '@tabler/icons-react';
import { api } from '../../shared/api';
import { useConfirm } from '../../shared/ConfirmContext';
import { formatDateTime, formatRelativeTime } from '../../shared/utils/dateUtils';

const sessionsKey = ['active-sessions'];

const ActiveSessionsTab = () => {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const queryClient = useQueryClient();

  const { data: sessions = [], isLoading, error } = useQuery({
    queryKey: sessionsKey,
    queryFn: () => api.get('accounts/api/auth/sessions/'),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: sessionsKey });

  const revoke = useMutation({
    mutationFn: (id) => api.post(`accounts/api/auth/sessions/${id}/revoke/`, {}),
    onSuccess: invalidate,
  });

  const revokeOthers = useMutation({
    mutationFn: () => api.post('accounts/api/auth/sessions/revoke_others/', {}),
    onSuccess: invalidate,
  });

  const handleRevoke = async (session) => {
    if (!await confirm(t('profile.sessions.confirmRevoke', { device: session.device_label || t('profile.sessions.unknownDevice') }))) return;
    revoke.mutate(session.id);
  };

  const handleRevokeOthers = async () => {
    if (!await confirm(t('profile.sessions.confirmRevokeOthers'))) return;
    revokeOthers.mutate();
  };

  const otherSessionsCount = sessions.filter((session) => !session.is_current).length;

  return (
    <div>
      <div className="d-flex align-items-start justify-content-between mb-4 flex-wrap gap-2">
        <div>
          <h4 className="mb-2">{t('profile.sessions.title')}</h4>
          <p className="text-muted mb-0">{t('profile.sessions.description')}</p>
        </div>
        {otherSessionsCount > 0 && (
          <Button variant="outline-danger" size="sm" onClick={handleRevokeOthers} disabled={revokeOthers.isPending}>
            <IconLogout2 size={16} className="me-1" />
            {t('profile.sessions.revokeOthers')}
          </Button>
        )}
      </div>

      {(revoke.isError || revokeOthers.isError) && (
        <Alert variant="danger">{t('profile.sessions.actionError')}</Alert>
      )}

      {isLoading ? (
        <div className="text-center py-4"><Spinner size="sm" /></div>
      ) : error ? (
        <Alert variant="danger">{t('profile.sessions.loadError')}</Alert>
      ) : sessions.length === 0 ? (
        <p className="text-muted">{t('profile.sessions.noSessions')}</p>
      ) : (
        <div className="table-responsive">
          <Table className="table-vcenter mb-0">
            <thead>
              <tr>
                <th>{t('profile.sessions.device')}</th>
                <th>{t('profile.sessions.ipAddress')}</th>
                <th>{t('profile.sessions.createdAt')}</th>
                <th>{t('profile.sessions.lastSeen')}</th>
                <th className="text-end">{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {sessions.map((session) => (
                <tr key={session.id}>
                  <td>
                    <div className="d-flex align-items-center gap-2">
                      <IconDeviceDesktop size={18} className="text-muted flex-shrink-0" />
                      <span className="fw-medium">{session.device_label || t('profile.sessions.unknownDevice')}</span>
                      {session.is_current && <Badge bg="success">{t('profile.sessions.thisDevice')}</Badge>}
                    </div>
                  </td>
                  <td className="text-muted">{session.ip_address || '—'}</td>
                  <td className="text-muted">{formatDateTime(session.created_at)}</td>
                  <td className="text-muted" title={formatDateTime(session.last_seen_at)}>
                    {formatRelativeTime(session.last_seen_at)}
                  </td>
                  <td className="text-end">
                    {!session.is_current && (
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleRevoke(session)}
                        disabled={revoke.isPending}
                      >
                        {t('profile.sessions.revoke')}
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </div>
  );
};

export default ActiveSessionsTab;
